import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Chip, CircularProgress, Button, IconButton, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useAuth } from '../../../../contexts/AuthContext';
import { listMockups, loadDraft, clearDraft, createMockupApp, updateMockupApp } from '../services/mockupService';
import { db, storage } from '../../../../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { jsxToHtml } from '../../lib/jsxToHtml';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { APPLAB_ACCENT, APPLAB_ORANGE } from '../../constants';

// ── Beräkna hur långt ett utkast kommit ──────────────────────────────────────
function draftProgress(d) {
  if (!d) return 0;
  if (d.brief?.description?.trim().length > 10) return 3;
  if (d.context?.customer_name?.trim()) return 2;
  return 1;
}

// ── Formattera datum ──────────────────────────────────────────────────────────
function fmtDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
}

// ── Statuskort (dina mockups + team-inspiration) ──────────────────────────────
const STATUS_CHIP = {
  done:       { label: 'Klar',       bg: APPLAB_ACCENT + '22', color: APPLAB_ACCENT },
  generating: { label: 'Genererar…', bg: 'rgba(255,200,0,0.12)', color: '#ffd740' },
  error:      { label: 'Fel',        bg: 'rgba(239,83,80,0.12)', color: '#ef5350' },
};

function MockupCard({ app, onClick, onSimilar, isTeam }) {
  const chip = STATUS_CHIP[app.status] || STATUS_CHIP.done;
  const customer = app.mockup_data?.context?.customer_name || app.tagline || '';

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2.5, borderRadius: 2.5, cursor: onClick ? 'pointer' : 'default',
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        transition: 'all 0.15s',
        display: 'flex', flexDirection: 'column', gap: 1.5,
        '&:hover': onClick ? { borderColor: APPLAB_ACCENT + '55', bgcolor: APPLAB_ACCENT + '08' } : {},
      }}
    >
      {/* Top row */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography fontSize="1.8rem" lineHeight={1}>{app.emoji || '✨'}</Typography>
          <Box>
            <Typography fontWeight={700} fontSize="0.88rem" sx={{ color: 'white', lineHeight: 1.3 }}>
              {app.name?.replace('MockupN – ', '') || 'Namnlös mockup'}
            </Typography>
            {customer && (
              <Typography sx={{ color: '#555', fontSize: '0.74rem', mt: 0.2 }}>{customer}</Typography>
            )}
          </Box>
        </Box>
        <Chip
          label={chip.label}
          size="small"
          sx={{ bgcolor: chip.bg, color: chip.color, fontSize: '0.68rem', height: 20, fontWeight: 600, flexShrink: 0 }}
        />
      </Box>

      {/* Date */}
      <Typography sx={{ color: '#444', fontSize: '0.72rem' }}>
        {fmtDate(app.updatedAt || app.createdAt)}
      </Typography>

      {/* Team: "Skapa liknande"-knapp */}
      {isTeam && (
        <Box
          onClick={e => { e.stopPropagation(); onSimilar?.(app); }}
          sx={{
            mt: 'auto', pt: 1,
            color: APPLAB_ACCENT, fontSize: '0.75rem', fontWeight: 600,
            cursor: 'pointer', '&:hover': { opacity: 0.8 },
          }}
        >
          Skapa liknande →
        </Box>
      )}
    </Box>
  );
}

// ── Utkast-kort ───────────────────────────────────────────────────────────────
function DraftCard({ draft, onContinue, onDelete }) {
  const step     = draftProgress(draft);
  const customer = draft.context?.customer_name;
  const savedAt  = draft.last_saved_at;

  return (
    <Box sx={{
      p: 2.5, borderRadius: 2.5,
      bgcolor: APPLAB_ACCENT + '08',
      border: `1px solid ${APPLAB_ACCENT}33`,
      display: 'flex', flexDirection: 'column', gap: 2,
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography fontWeight={700} sx={{ color: 'white', fontSize: '0.9rem', mb: 0.5 }}>
            {customer || 'Kund ej vald'}
          </Typography>
          <Typography sx={{ color: '#666', fontSize: '0.75rem' }}>
            Steg {step} av 4 · Sparat {savedAt ? `kl ${fmtTime(savedAt)}` : ''}
          </Typography>
        </Box>
        <Tooltip title="Radera utkast">
          <IconButton onClick={onDelete} size="small" sx={{ color: '#555', '&:hover': { color: '#ef5350' } }}>
            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Step dots */}
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
        {[1, 2, 3, 4].map(n => (
          <Box key={n} sx={{
            width: n <= step ? 20 : 8, height: 8, borderRadius: 4,
            bgcolor: n <= step ? APPLAB_ACCENT : 'rgba(255,255,255,0.1)',
            transition: 'width 0.3s',
          }} />
        ))}
        <Typography sx={{ color: '#555', fontSize: '0.7rem', ml: 0.5 }}>
          {['', 'Kund vald', 'Brief skriven', 'Stil vald', 'Redo'][step]}
        </Typography>
      </Box>

      <Button
        onClick={onContinue}
        variant="contained"
        size="small"
        sx={{
          bgcolor: APPLAB_ACCENT, fontWeight: 700, textTransform: 'none',
          '&:hover': { bgcolor: '#00796B' }, alignSelf: 'flex-start',
        }}
      >
        Fortsätt →
      </Button>
    </Box>
  );
}

// ── Hur-det-fungerar ──────────────────────────────────────────────────────────
const HOW_STEPS = [
  { icon: '✍️', title: 'Beskriv vad du vill',  desc: 'Berätta för kunden, situationen och vad mockupen ska visa.' },
  { icon: '✨', title: 'AI bygger åt dig',       desc: 'Claude genererar ett komplett HTML-gränssnitt med realistisk data på 15–30 sekunder.' },
  { icon: '🔗', title: 'Dela med kunden',        desc: 'Lägg till i en PitchN-presentation eller dela direkt som länk.' },
];

// ── Landningssida ─────────────────────────────────────────────────────────────
export default function MockupLanding() {
  const navigate        = useNavigate();
  const { currentUser } = useAuth();

  const [draft, setDraft]       = useState(null);
  const [myMockups, setMy]      = useState([]);
  const [teamMockups, setTeam]  = useState([]);
  const [loading, setLoading]   = useState(true);
  const [, setDeleting]         = useState(false);
  const [importing, setImporting] = useState(false);
  const jsxInputRef             = useRef(null);

  useEffect(() => {
    if (!currentUser) return;
    Promise.all([
      loadDraft(currentUser.uid).catch(() => null),
      listMockups(currentUser.uid).catch(() => []),
      // Team mockups: done mockups from others, limit 6
      getDocs(query(
        collection(db, 'applab_apps'),
        where('type', '==', 'mockup'),
        where('status', '==', 'done'),
        orderBy('createdAt', 'desc'),
        limit(12),
      )).then(snap => snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(a => a.createdBy !== currentUser.uid)
        .slice(0, 6)
      ).catch(() => []),
    ]).then(([d, mine, team]) => {
      setDraft(d);
      setMy(mine);
      setTeam(team);
    }).finally(() => setLoading(false));
  }, [currentUser]);

  async function deleteDraft() {
    setDeleting(true);
    try { await clearDraft(currentUser.uid); } catch (_) {}
    setDraft(null);
    setDeleting(false);
  }

  async function handleJsxUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const html = jsxToHtml(text, file.name);
      const blob = new Blob([html], { type: 'text/html' });
      const storageRef = ref(storage, `applab/mockups/jsx/${currentUser.uid}/${Date.now()}_${file.name}.html`);
      await uploadBytes(storageRef, blob, { contentType: 'text/html' });
      const publicUrl = await getDownloadURL(storageRef);
      const basename = file.name.replace(/\.(jsx?|tsx?)$/, '');
      const appId = await createMockupApp({
        uid: currentUser.uid,
        displayName: currentUser.displayName,
        name: `MockupN – ${basename}`,
        mockup_data: {
          context:  { customer_name: basename, customer_id: null, uppdrag_id: null, situation: 'early' },
          brief:    { description: 'Importerad JSX-fil', reference_links: [] },
          style:    { preset: 'polished', colors: { detected: null, selected: null, mode: 'auto' }, tone: 0 },
          branding: { customer_branding_ref: null, adherence: 'off', selected_colors: [], selected_fonts: { heading: '', body: '' } },
          systems:  { registered: [], custom: [] },
          current_version: null, versions_count: 0,
          jsx_imported: true, jsx_filename: file.name,
        },
      });
      await updateMockupApp(appId, { status: 'done', public_url: publicUrl });
      navigate(`/applab/mockup/${appId}/edit`);
    } catch (err) {
      console.error('JSX import failed:', err);
      alert('Kunde inte importera filen. Kontrollera att det är en giltig JSX-fil.');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  }

  function startSimilar(app) {
    // Navigate to wizard pre-filled with similar context (brief + style from the app)
    navigate('/applab/mockup/new', {
      state: {
        prefill: {
          brief:   app.mockup_data?.brief,
          style:   app.mockup_data?.style,
          systems: app.mockup_data?.systems,
        },
      },
    });
  }

  const hasDraft   = draft && draftProgress(draft) > 0;
  const hasOwn     = myMockups.length > 0;
  const hasTeam    = teamMockups.length > 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#111', color: 'white', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', borderBottom: '1px solid rgba(255,255,255,0.06)', px: 3, py: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            onClick={() => navigate('/applab')}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', color: '#888', '&:hover': { color: 'white' }, fontSize: '0.85rem' }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} /> App<span style={{ color: APPLAB_ORANGE }}>LAB</span>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, maxWidth: 900, width: '100%', mx: 'auto', px: { xs: 2, sm: 3 }, py: 5 }}>

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <Box sx={{ mb: 6, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Typography variant="h4" fontWeight={900} sx={{ lineHeight: 1 }}>MockupN</Typography>
              <Box sx={{ fontSize: '0.65rem', fontWeight: 700, color: APPLAB_ACCENT, bgcolor: APPLAB_ACCENT + '22', px: 1, py: 0.3, borderRadius: 1, border: `1px solid ${APPLAB_ACCENT}44` }}>
                BETA
              </Box>
            </Box>
            <Typography sx={{ color: '#666', fontSize: '0.92rem' }}>
              Skapa visuella mockups för kunddialoger — AI bygger hela HTML-gränssnittet åt dig.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              onClick={() => navigate('/applab/mockup/new')}
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ bgcolor: APPLAB_ACCENT, fontWeight: 700, textTransform: 'none', px: 3, py: 1.2, fontSize: '0.9rem', '&:hover': { bgcolor: '#00796B' } }}
            >
              Skapa ny mockup
            </Button>
            <Button
              onClick={() => jsxInputRef.current?.click()}
              variant="outlined"
              startIcon={importing ? <CircularProgress size={16} sx={{ color: APPLAB_ACCENT }} /> : <UploadFileIcon />}
              disabled={importing}
              sx={{ borderColor: 'rgba(255,255,255,0.15)', color: '#aaa', textTransform: 'none', fontWeight: 600,
                '&:hover': { borderColor: APPLAB_ACCENT + '88', color: APPLAB_ACCENT, bgcolor: APPLAB_ACCENT + '0a' } }}
            >
              {importing ? 'Importerar…' : 'Importera JSX'}
            </Button>
            <input
              ref={jsxInputRef}
              type="file"
              accept=".jsx,.js,.tsx,.ts"
              style={{ display: 'none' }}
              onChange={handleJsxUpload}
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: APPLAB_ACCENT }} />
          </Box>
        ) : (
          <>
            {/* ── Pågående utkast ──────────────────────────────────────────── */}
            {hasDraft && (
              <Box sx={{ mb: 5 }}>
                <Typography fontWeight={800} fontSize="0.95rem" sx={{ color: 'white', mb: 2 }}>
                  Pågående utkast
                </Typography>
                <Box sx={{ maxWidth: 360 }}>
                  <DraftCard
                    draft={draft}
                    onContinue={() => navigate('/applab/mockup/new')}
                    onDelete={deleteDraft}
                  />
                </Box>
              </Box>
            )}

            {/* ── Dina mockups ─────────────────────────────────────────────── */}
            <Box sx={{ mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography fontWeight={800} fontSize="0.95rem" sx={{ color: 'white' }}>
                    Dina mockups
                  </Typography>
                  {hasOwn && (
                    <Chip label={myMockups.length} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#888', height: 20, fontSize: '0.68rem' }} />
                  )}
                </Box>
              </Box>

              {hasOwn ? (
                <Grid container spacing={2}>
                  {myMockups.map(app => (
                    <Grid item xs={12} sm={6} md={4} key={app.id}>
                      <MockupCard
                        app={app}
                        onClick={() => navigate(`/applab/mockup/${app.id}/edit`)}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box
                  onClick={() => navigate('/applab/mockup/new')}
                  sx={{
                    p: 4, borderRadius: 2.5, cursor: 'pointer', textAlign: 'center',
                    border: '1px dashed rgba(255,255,255,0.08)',
                    bgcolor: 'rgba(255,255,255,0.02)',
                    '&:hover': { borderColor: APPLAB_ACCENT + '55', bgcolor: APPLAB_ACCENT + '06' },
                    transition: 'all 0.15s',
                  }}
                >
                  <Typography fontSize="2rem" mb={1}>✨</Typography>
                  <Typography fontWeight={700} sx={{ color: '#888', fontSize: '0.88rem', mb: 0.5 }}>
                    Du har inga mockups än
                  </Typography>
                  <Typography sx={{ color: '#555', fontSize: '0.8rem' }}>
                    Klicka här för att skapa din första
                  </Typography>
                </Box>
              )}
            </Box>

            {/* ── Inspiration från teamet ───────────────────────────────────── */}
            {hasTeam && (
              <Box sx={{ mb: 6 }}>
                <Typography fontWeight={800} fontSize="0.95rem" sx={{ color: 'white', mb: 0.5 }}>
                  Inspiration från teamet
                </Typography>
                <Typography sx={{ color: '#555', fontSize: '0.78rem', mb: 2 }}>
                  Klicka på en mockup för att se den, eller använd den som utgångspunkt för din egen.
                </Typography>
                <Grid container spacing={2}>
                  {teamMockups.map(app => (
                    <Grid item xs={12} sm={6} md={4} key={app.id}>
                      <MockupCard
                        app={app}
                        onClick={() => navigate(`/applab/mockup/${app.id}/edit`)}
                        onSimilar={startSimilar}
                        isTeam
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* ── Hur det fungerar ──────────────────────────────────────────── */}
            <Box sx={{ mt: 2, pt: 4, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <Typography fontWeight={800} fontSize="0.95rem" sx={{ color: '#555', mb: 3 }}>
                Hur det fungerar
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {HOW_STEPS.map((s, i) => (
                  <Box key={i} sx={{ flex: '1 1 200px', display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1rem',
                    }}>
                      {s.icon}
                    </Box>
                    <Box>
                      <Typography fontWeight={700} sx={{ color: '#888', fontSize: '0.82rem', mb: 0.3 }}>{s.title}</Typography>
                      <Typography sx={{ color: '#555', fontSize: '0.76rem', lineHeight: 1.6 }}>{s.desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Typography
                sx={{ mt: 3, color: '#444', fontSize: '0.75rem', cursor: 'pointer', '&:hover': { color: APPLAB_ACCENT } }}
                onClick={() => navigate('/applab/docs')}
              >
                AppLAB-dokumentation →
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
